import { motion } from "framer-motion";
import { useState } from "react";

const features = [
    { tag: "Workflows", title: "Your workflows, not ours.", body: "Every firm runs intake differently. We map your actual process, CMS fields, telephony routing, and intake checklist — and build agents that match. No forced templates.", icon: "fa-diagram-project" },
    { tag: "Voice", title: "Your voice, not a generic bot.", body: "Agents are tuned to how your firm sounds. The greeting, the tone, the questions your intake specialists ask — with performance that compounds over time. A caller in the worst week of their life should hear your firm — not a script.", icon: "fa-microphone-lines" },
    { tag: "Integrations", title: "Your stack, done right.", body: "Native connections to Litify, Filevine, SmartAdvocate, RingCentral, Five9, Dialpad, Zoom — and the rest of the systems you already run.", icon: "fa-plug-circle-check" },
    { tag: "Security", title: "SOC 2 + HIPAA-aligned.", body: "SOC 2 Type I today, Type II underway. End-to-end encryption in transit and at rest. US-hosted infrastructure. Your data never trains shared models.", icon: "fa-shield-halved" },
    { tag: "Control", title: "Humans stay in control.", body: "Every agent runs against confidence thresholds you set. High-confidence executes; uncertain flags for review. Pause, override, or roll back any workflow in one click. Because it's your name a caller hears first.", icon: "fa-user-shield" },
    { tag: "Reliability", title: "99% uptime, real humans on call.", body: "Redundant infrastructure with 24/7 monitoring and automatic failover. Because the worst time for an outage is during your morning intake rush.", icon: "fa-tower-broadcast" },
];

const MOBILE_LIMIT = 3;

const PlatformSection = () => {
    const [showAll, setShowAll] = useState(false);
    return (
        <section id="platform" data-testid="platform-section" className="relative py-24 md:py-32 bg-[var(--cream-2)] border-t border-[var(--border)]">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mb-12 md:mb-14 max-w-4xl"
                >
                    <span className="eyebrow-pill">The platform</span>
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                        We don&apos;t sell an AI product. We deploy <span className="accent-blue">agents</span> to address the areas that are holding you back.
                    </h2>
                    <p className="mt-5 text-lg text-[var(--ink-soft)] max-w-2xl">
                        A visual, legal-domain-native agent builder that turns your processes into production agents in days — not months.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {features.map((f, i) => (
                        <motion.div
                            key={f.tag}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                            whileHover={{ y: -4 }}
                            data-testid={`platform-feature-${i}`}
                            className={`card-paper p-7 md:p-8 group ${
                                i >= MOBILE_LIMIT && !showAll ? "hidden md:block" : ""
                            }`}
                        >
                            <div className="flex items-center justify-between mb-7">
                                <span className="font-mono text-[10px] tracking-[0.18em] text-[var(--muted)] uppercase">{f.tag}</span>
                                <i className={`fa-solid ${f.icon} accent-coral opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all`} />
                            </div>
                            <h3 className="font-display text-[1.5rem] md:text-2xl text-[var(--ink)] mb-3 leading-tight">{f.title}</h3>
                            <p className="text-[var(--ink-soft)] leading-relaxed text-[15px]">{f.body}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Mobile-only "Show more" toggle */}
                {!showAll && features.length > MOBILE_LIMIT && (
                    <div className="mt-8 flex justify-center md:hidden">
                        <button
                            type="button"
                            onClick={() => setShowAll(true)}
                            data-testid="platform-show-more"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] text-[var(--ink)] font-medium text-sm hover:bg-[var(--ink)] hover:text-white hover:border-[var(--ink)] transition-colors"
                        >
                            Show {features.length - MOBILE_LIMIT} more
                            <i className="fa-solid fa-arrow-down text-xs" />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default PlatformSection;
